// The Productivity Chart's palette: the colours of the quantities it plots, and the
// colours of the frame it plots them in.

import { withAlpha } from '@/plugins/highcharts/chartColors';
import colors from '@/plugins/tailwind';

// What each measured quantity looks like on a chart, in one place.
//
// The rule the map encodes: a colour belongs to a *domain*, not to a series. Green is the
// weight/growth domain, blue is water, red means "a reference line or an alarm" and nothing else,
// and grey is for counts and targets - things read against a curve rather than states that demand
// action. That reservation is the reason `deadCount` is slate rather than red: a dead count is a
// figure you read, and if red also means "count" then a red alarm pin stops meaning "act".
//
// The green trio comes from the backend's own chart strategies (CrownData-API,
// `WeightChartUtils`), which use a single green for weight, Daily Gain and Growth Index alike -
// affordable there because each has a chart to itself. Here all three can be on screen at once,
// and `ProductivityReadoutRow` puts their colour dots side by side in one row, so they take
// three shades of that same green instead: same domain, still separable at a glance.
//
// No hex literals below: every value is a resolved Tailwind theme token, so a colour is changed in
// the theme once and every chart reading from this map follows. The handful of colours with no
// existing token live under `chart` in `tailwind.config.js`.

/** One colour per plotted metric. */
export const CHART_SERIES_COLORS = {
    weight: colors.chart.weight,
    dailyGain: colors.chart.dailyGain,
    growthIndex: colors.chart.growthIndex,
    waterActual: colors.chart.water,
    // A target rather than a reading, so it stays neutral and lets the measured column in front of
    // it carry the colour.
    waterNorm: colors.slate[300],
    // Deeper water blue rather than red: a column poking above the norm means *more water*, which
    // is a fact about consumption, not an alarm.
    waterOverNorm: colors.sky[700],
    deadCount: colors.slate[600],
} as const;

/**
 * The Water Norm as *text* rather than as a column. Its own fill is a pale neutral chosen to sit
 * behind a solid column without competing with it, which at 11px on white is barely visible - so a
 * label quoting the norm takes the darker step of the same ramp instead. Still subordinate to the
 * measurement it is read against; just legible.
 */
export const WATER_NORM_TEXT_COLOR = colors.slate[500];

/**
 * A comparison reference figure as *text*, on the chart. The halo the figure belongs to is drawn at a
 * third of its metric's own opacity, which is right for a wide stroke behind a curve and invisible as
 * 11px type - so the number takes a neutral instead. Neutral rather than a muted step of each
 * metric's own hue: six hues muted far enough to read as subordinate stop being separable from one
 * another, and the number is already tied to its metric by sitting on that metric's own halo.
 */
export const COMPARISON_REFERENCE_TEXT_COLOR = colors.gray[500];

/** The Growth Index's 100 baseline, and any other "this is the target" line. */
export const CHART_REFERENCE_COLORS = {
    line: colors.red[400],
    // The line's own colour is ~3.5:1 on white - fine for a 2px stroke, under AA for a 10px label,
    // so the caption beside it takes the darker step of the same ramp.
    label: colors.error[700],
} as const;

/**
 * Note and Alarm pins. An Alarm carries state - whether a Cause has been recorded against it, which
 * is as close as the schema gets to "someone has dealt with this" - so it has two appearances; a
 * Note has none, being a written observation rather than a condition anyone resolves.
 */
export const CHART_EVENT_COLORS = {
    alarmUnhandled: { stroke: colors.red[400], fill: colors.error[100], text: colors.error[800] },
    alarmHandled: { stroke: colors.error[300], fill: colors.error[50], text: colors.error[800] },
    note: { stroke: colors.chart.note, fill: colors.chart.noteFill, text: colors.chart.noteText },
} as const;

/**
 * The thin context bands stacked under the plot - the Production Groups a range spans, and the Feed Mix
 * blocks running through them. Both are drawn as tinted chips (pale fill, saturated outline, dark
 * ink) rather than solid slabs: they are context for the series above them, and a saturated
 * full-width bar reads as the loudest thing on the chart when it is the quietest thing on it. Same
 * treatment for both so they read as one family; different hues so they are told apart at a glance
 * rather than merely being adjacent.
 */
export const CHART_CONTEXT_BAND_COLORS = {
    productionGroup: { fill: colors.chart.productionGroupBand, border: colors.chart.productionGroupBandBorder, text: colors.chart.productionGroupBandText },
    // Slate, not a second blue - the Feed Mix band sits directly under the Production Group band and
    // would otherwise be read as part of it.
    feedMix: { fill: colors.slate[200], border: colors.slate[400], text: colors.slate[600] },
} as const;

/**
 * The vertical scaffolding behind the series. Two tiers, deliberately: a Production Group boundary is
 * the most important division on the axis - week-in-period numbering restarts there, so without it
 * a range spanning two periods reads as a mistake - and it has to win against the calendar rules,
 * which are only there to make a span skimmable.
 */
export const CHART_AXIS_RULE_COLORS = {
    productionGroupBoundary: colors.slate[400],
    // The horizontal rule between two stacked bands. Lighter than the boundary above it and than
    // any series: it divides the panel rather than saying anything about the data, so it has to be
    // visible while being the least prominent line on screen.
    bandSeparator: colors.slate[300],
    // An alpha wash rather than a token: it has to sit *under* the series without tinting them, and
    // no opaque grey does that against both the white plot and the tinted context bands.
    calendarRule: 'rgba(0, 0, 0, 0.12)',
} as const;

// The chart's own chrome, as opposed to its data: axis lines and labels, band captions, the
// navigator strip, the readout crosshair. Gathered here and resolved from the Tailwind theme so the
// frame keeps one voice across a chart with four stacked bands and two axes - and so that changing
// what a caption weighs against a tick label is one edit rather than a hunt through the options
// tree. Anything with no exact theme token stays a literal but keeps a name.
export const CHART_CHROME_COLORS = {
    // Tick labels on the data bands and under the navigator - the frame's own numbers, a step
    // lighter than anything carrying a word.
    axisLabel: colors.gray[400],
    // Text that says something: a band's caption, the x axis' day/week labels, the caption on the
    // "reference runs past today" rule. A step darker, because it is read rather than glanced at.
    labelText: colors.gray[500],
    // Every edge of the frame at one weight - the x axis line, its ticks, the navigator's outline.
    axisLine: colors.gray[200],
    // The horizontal grid inside a data band. The faintest line drawn: it exists to carry the eye
    // across to a value, not to be read itself.
    bandGridLine: colors.gray[100],
    navigatorHandleFill: colors.white,
    navigatorHandleBorder: colors.slate[400],
    // The note an empty band carries in place of data, and the dashed rule marking today when a
    // reference runs past it. Both are absences rather than measurements, so they sit at the
    // frame's weight rather than a series'.
    emptyBandNote: colors.gray[400],
    referenceNowRule: colors.gray[400],
    // The two washes the frame lays over the plot rather than draws on it: the navigator's
    // out-of-range mask, and the shading over the stretch a reference runs past today. Both have
    // to read as outside-what-you-are-looking-at while whatever sits under them stays legible, so
    // they are the frame's own slate at low opacity rather than an opaque grey.
    navigatorMask: withAlpha(colors.slate[400], 0.18),
    referencePastNowShading: withAlpha(colors.slate[400], 0.12),
    // The readout crosshair. Following the cursor it is frame; pinned it is drawn heavier and in the
    // solution's own accent, so the line itself says the figures beside it are being held. Taken from
    // the palette rather than written as a hex: the accent is the one colour that differs per brand.
    crosshair: colors.gray[400],
    pinnedCrosshair: colors.primary[800],
    // Last resorts for a cursor label or an event tooltip's swatch whose point and series both
    // arrive without a colour of their own. Ink weight, so nothing renders invisible.
    cursorLabelFallback: colors.gray[700],
    eventSwatchFallback: colors.gray[500],
} as const;
